import React from 'react';

const SolutionSection: React.FC = () => {
  return (
    <section className="py-24 px-4 bg-gradient-to-br from-white via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 mb-8 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-full text-sm font-semibold shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
            ✨ 완벽한 해결책
          </div>
          
          <h2 className="text-5xl md:text-6xl font-black mb-8">
            <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              매주 AI 트렌드 추적
            </span>
            <br />
            <span className="text-gray-800">핵심 요약</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            복잡한 AI 정보를 간단하고 명확하게 정리해드립니다
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            {/* Global Monitoring Card */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative bg-white p-10 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                    <span className="text-2xl">🌍</span>
                  </div>
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    글로벌 모니터링
                  </h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-center text-lg">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-gray-700 font-semibold">국내외 유명 커뮤니티</span>
                  </li>
                  <li className="flex items-center text-lg">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-gray-700 font-semibold">국내외 유명 IT 리더</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 5-Minute Read Card */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative bg-white p-10 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4">
                    <span className="text-2xl">⚡</span>
                  </div>
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    5분 완독
                  </h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-center text-lg">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-gray-700 font-semibold">핵심 요약</span>
                  </li>
                  <li className="flex items-center text-lg">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-gray-700 font-semibold">트렌드 의견 추가</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center lg:text-left">
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-700 text-white p-12 rounded-3xl shadow-2xl">
                <h3 className="text-4xl font-black mb-8 leading-tight">
                  진짜 AI 최전선을<br />
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    만나세요
                  </span>
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mr-4">
                      <span className="text-purple-900 text-xl">🚀</span>
                    </div>
                    <span className="text-xl font-semibold text-purple-100">
                      Reddit, Hacker News 실시간 추적
                    </span>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mr-4">
                      <span className="text-purple-900 text-xl">👑</span>
                    </div>
                    <span className="text-xl font-semibold text-purple-100">
                      AI 업계 리더들의 최신 인사이트
                    </span>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mr-4">
                      <span className="text-purple-900 text-xl">🇰🇷</span>
                    </div>
                    <span className="text-xl font-semibold text-purple-100">
                      한국어로 완벽 요약 제공
                    </span>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full"></div>
                <div className="absolute bottom-4 left-4 w-16 h-16 bg-white/10 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
